import React, { useCallback, useState } from 'react';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';

import CopyS3PathButton from '../common/CopyS3PathButton';
import When from '../common/When';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import { showErrorNotification } from '../../notifications';
import { downloadManualImportFile } from '../../services/manualImportDownload.service';
import { downloadBlob } from '../../utility/downloadBlob';

type ManualImportFileCellProps = {
    logId: string;
    fileName: string;
    s3Key?: string | null;
    canDownload: boolean;
};

function ManualImportFileCell({
    logId,
    fileName,
    s3Key,
    canDownload,
}: ManualImportFileCellProps) {
    const [loading, setLoading] = useState(false);

    const handleDownload = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            setLoading(true);

            try {
                const result = await downloadManualImportFile(logId);
                downloadBlob(result.blob, result.fileName || fileName);
            } catch (error: unknown) {
                showErrorNotification(getErrorMessage(error));
            } finally {
                setLoading(false);
            }
        },
        [fileName, logId],
    );

    return (
        <Group wrap="nowrap" justify="space-between" gap={6} className="w-full">
            <Text
                size="sm"
                truncate="end"
                title={fileName}
                className="min-w-0 flex-1"
            >
                {fileName}
            </Text>
            <Group gap={4} wrap="nowrap" className="shrink-0">
                <When condition={canDownload}>
                    <Tooltip label="Download file" withArrow openDelay={300}>
                        <span className="inline-flex">
                            <ActionIcon
                                variant="subtle"
                                size="sm"
                                className="text-primary hover:bg-primary/10"
                                onClick={handleDownload}
                                loading={loading}
                                aria-label="Download file"
                            >
                                <IconDownload size={16} />
                            </ActionIcon>
                        </span>
                    </Tooltip>
                </When>
                <CopyS3PathButton path={s3Key} />
            </Group>
        </Group>
    );
}

export default ManualImportFileCell;
